import { c as _c } from "react/compiler-runtime";
import { IconAlertWarningOutline18, IconArrowUpRightOutline18, IconCheckOutline18, IconCircleInfoOutline18, IconClockOutline18, IconGasPumpOutline18, IconMinusOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import { OrganizationWalletBadge } from "@/components/ui/OrganizationWalletBadge";
import { TokenIcon } from "@/components/ui/token-icon";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { formatRRule, formatTokenAmount } from "@/components/ui/workflow-step-row";
import { ChainBadge } from "@/components/ui/workflow-step-row/components/ChainBadge";
import { findMatchingOrganizationWallet, InlineDetectedValue, isChainIdKey, isEthAddress, type InlineTokenData } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { containsTemplateVariable, inlineReferenceBadgeAccentClassName, inlineReferenceBadgeClassName, renderValueWithVariables } from "@/components/ui/workflow-step-row/components/VariablePill";
import { getStatusLabel } from "@/hooks/useNodeExecutionStatus";
import { formatX402Price } from "@/hooks/useX402Endpoints";
import { getNativeTokenDecimals } from "@/lib/chains";
import type { TelegramChat } from "@/lib/telegramBot";
import { cn } from "@/lib/utils";
import type { WorkflowDefinition } from "@/stores/workflowsStore";
import type { NodeExecutionStatus } from "@/types/runStream";
import type { OrganizationWallet } from "@/types/wallet";
import { AMOUNT_FIELDS } from "@/utils/resolveDescriptionTemplates";
import { camelToWords, formatLabel } from "@/utils/string";
import { PixelLoader } from "@/components/ui/pixel-loader";
import Link from "next/link";
import React from "react";
import { calculateDuration, extractTemplateVariableNames, formatDelayTime, formatDisplayUrl, formatMathOperation, formatValue, isHexColor, isHexLikeValue, renderWithVariableHighlights, TemplateMarkdownPreview, tooltipText, truncateHexValue, type ChainInfo, type TokenInfo } from "./ActionNodeValueFormat";
import { formatCurrencyUsd } from "@/lib/format/currency";
function getSchemaFieldFormat(schema: Record<string, unknown> | undefined, key: string): string | undefined {
  const properties = (schema as {
    properties?: Record<string, {
      format?: string;
    }>;
  })?.properties;
  return properties?.[key]?.format;
}
import { resolveQuickBooksAccountLabel, type QBAccount } from "./form-fields/quickbooksAccounts";
import { resolveSlackConversationLabel, type SlackChannel } from "./form-fields/slackChannels";
import { resolveTelegramChatLabel } from "./form-fields/telegramChats";
import NodeOutputPreview from "./NodeOutputPreview";
import { getActionNodeSummary } from "./nodeSummaries";
import { useOrgWalletsContext } from "./OrganizationWalletsContext";
import { WorkflowMinimap } from "./WorkflowMinimap";

// ─── Execution Status Badge ─────────────────────────────────────────────────

interface ExecutionStatusBadgeProps {
  executionStatus: NodeExecutionStatus;
}

/**
 * Renders the execution status badge icon (top-left corner of node).
 * Shown when there is an active run and the node is not idle.
 */
export function ExecutionStatusBadge(t0) {
  const $ = _c(21);
  const {
    executionStatus
  } = t0;
  const t1 = `Node status: ${getStatusLabel(executionStatus)}`;
  const t2 = executionStatus === "running" && "border-blue-500 bg-blue-100/90 text-blue-600 dark:bg-blue-900/70 dark:text-blue-300";
  const t3 = executionStatus === "auto_swapping" && "border-violet-500 bg-violet-100/90 text-violet-600 dark:bg-violet-900/70 dark:text-violet-300";
  const t4 = executionStatus === "success" && "border-green-500 bg-green-100/90 text-green-600 dark:bg-green-900/70 dark:text-green-300";
  const t5 = executionStatus === "failure" && "border-red-500 bg-red-100/90 text-red-600 dark:bg-red-900/70 dark:text-red-300";
  const t6 = executionStatus === "skipped" && "bg-ds-neutral-100/90 text-ds-text-muted dark:bg-ds-neutral-800/70 border-ds-border-default";
  let t7;
  if ($[0] !== t2 || $[1] !== t3 || $[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = cn("shadow-ds-soft absolute -left-3 -top-3 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 backdrop-blur-md", t2, t3, t4, t5, t6);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== executionStatus) {
    t8 = (executionStatus === "running" || executionStatus === "auto_swapping") && <PixelLoader size="xs" aria-label="Running" />;
    $[6] = executionStatus;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== executionStatus) {
    t9 = executionStatus === "success" && <IconCheckOutline18 className="h-3 w-3" aria-hidden="true" />;
    $[8] = executionStatus;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== executionStatus) {
    t10 = executionStatus === "failure" && <IconXmarkOutline18 className="h-3 w-3" aria-hidden="true" />;
    $[10] = executionStatus;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== executionStatus) {
    t11 = executionStatus === "skipped" && <IconMinusOutline18 className="h-3 w-3" aria-hidden="true" />;
    $[12] = executionStatus;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t1 || $[15] !== t10 || $[16] !== t11 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t12 = <div role="status" aria-label={t1} className={t7}>{t8}{t9}{t10}{t11}</div>;
    $[14] = t1;
    $[15] = t10;
    $[16] = t11;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
}

// ─── Node Subtitle Row ──────────────────────────────────────────────────────

interface NodeSubtitleProps {
  needsConnector: boolean;
  isTemplateView?: boolean;
  requiredConnectorType?: string | null;
  showOutputPreview: boolean;
  isX402Endpoint: boolean;
  x402Info: {
    price?: string;
    tokenDecimals?: number;
    tokenSymbol?: string;
  } | null;
  executionStatus: NodeExecutionStatus;
  nodeState?: {
    startedAt?: string;
    finishedAt?: string;
  };
  requiresGas?: boolean;
  needsGasWarning: boolean;
  needsTokenWarning?: boolean;
}

/**
 * Renders the subtitle row below the node name (e.g. "Action", "Action output", connector warnings).
 */
export function NodeSubtitle({
  needsConnector,
  isTemplateView,
  requiredConnectorType,
  showOutputPreview,
  isX402Endpoint,
  x402Info,
  executionStatus,
  nodeState,
  requiresGas,
  needsGasWarning,
  needsTokenWarning
}: NodeSubtitleProps) {
  if (needsConnector) {
    if (isTemplateView) {
      return <p className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400">
          {requiredConnectorType === "wallet" ? "Using your wallet" : `Connect your ${requiredConnectorType || "account"}`}
        </p>;
    }
    return <p className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
        <IconAlertWarningOutline18 className="h-3 w-3" />
        Connector required
      </p>;
  }
  return <div className="micro-label flex items-center gap-1">
      <span>{showOutputPreview ? "Action output" : isX402Endpoint ? "x402 Action" : "Action"}</span>
      {/* Show execution time inline when output is displayed */}
      {showOutputPreview && <>
          <span>·</span>
          <span className={cn(executionStatus === "success" && "text-green-600 dark:text-green-400", executionStatus === "failure" && "text-red-600 dark:text-red-400")}>
            {calculateDuration(nodeState?.startedAt, nodeState?.finishedAt) || "Done"}
          </span>
        </>}
      {isX402Endpoint && x402Info?.price && !showOutputPreview && <>
          <span>·</span>
          <span className="text-blue-400 dark:text-blue-600">
            {formatX402Price(x402Info.price, x402Info.tokenDecimals)} {x402Info.tokenSymbol}
          </span>
        </>}
      {requiresGas && !showOutputPreview && <>
          <span>·</span>
          <div className={cn("micro-label flex h-5 items-center gap-1", needsGasWarning || needsTokenWarning ? "text-amber-500 dark:text-amber-400" : "text-blue-400 dark:text-blue-600")}>
            <IconGasPumpOutline18 className="h-3 w-3" />
            <span>{needsGasWarning ? "No gas" : needsTokenWarning ? "Missing token" : "Uses gas"}</span>
          </div>
        </>}
    </div>;
}

// ─── Scheduled Execution Banner ─────────────────────────────────────────────

interface ScheduledBannerProps {
  scheduledRelativeTime: string;
}
export function ScheduledBanner(t0) {
  const $ = _c(4);
  const {
    scheduledRelativeTime
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconClockOutline18 className="h-3 w-3 flex-shrink-0" />;
    t2 = <span className="font-medium">Scheduled: </span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== scheduledRelativeTime) {
    t3 = <div className="flex items-center gap-2 rounded-md bg-blue-50 p-2 text-xs text-blue-800 dark:bg-blue-950/30 dark:text-blue-300">{t1}<div>{t2}<span>{scheduledRelativeTime}</span></div></div>;
    $[2] = scheduledRelativeTime;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

// ─── Polymarket Requirements Banner ─────────────────────────────────────────

export interface PolymarketBannerProps {
  hasDepositWallet: boolean;
  hasPusd: boolean;
  onSetupWallet?: () => void;
  onFundWallet?: () => void;
}
export function PolymarketBanner(t0) {
  const $ = _c(9);
  const {
    hasDepositWallet,
    hasPusd,
    onSetupWallet,
    onFundWallet
  } = t0;
  if (hasDepositWallet && hasPusd) {
    return null;
  }
  const message = !hasDepositWallet ? "Set up a Polymarket deposit wallet." : "Requires: pUSD on Polygon.";
  const action = !hasDepositWallet ? onSetupWallet : onFundWallet;
  const actionLabel = !hasDepositWallet ? "Set up wallet" : "Fund wallet";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconCircleInfoOutline18 className="mt-0.5 h-3 w-3 flex-shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== message) {
    t2 = <span className="font-medium">{message}</span>;
    $[1] = message;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== action || $[4] !== actionLabel) {
    t3 = action ? <button type="button" onClick={action} className="underline hover:text-amber-900 dark:hover:text-amber-200">{actionLabel} →</button> : <a href="/wallet-management" className="underline hover:text-amber-900 dark:hover:text-amber-200" target="_blank" rel="noopener noreferrer">{actionLabel} →</a>;
    $[3] = action;
    $[4] = actionLabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">{t1}<div>{t2}{" "}{t3}</div></div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

// ─── Node Body Content ──────────────────────────────────────────────────────

interface NodeBodyContentProps {
  showOutputPreview: boolean;
  nodeState?: {
    result?: Record<string, unknown>;
  };
  executionStatus: NodeExecutionStatus;
  // Send ERC20 Token
  isSendErc20Token: boolean;
  payloadChainId?: number;
  tokenInfo: TokenInfo | null;
  payload?: Record<string, unknown>;
  chainInfoMap: Map<number, ChainInfo>;
  recipientAddress?: string;
  recipientProfile?: {
    address?: string;
  } | null;
  getNodeName: (nodeId: string) => string | undefined;
  // DEX Swap
  isDexSwap: boolean;
  tokenInInfo: TokenInfo | null;
  tokenOutInfo: TokenInfo | null;
  // Relay Swap
  isRelaySwap: boolean;
  srcChainId?: number;
  dstChainId?: number;
  // Math operation
  isMathOperation: boolean;
  // Trigger workflow
  isTriggerWorkflow: boolean;
  targetWorkflowId?: string;
  targetWorkflow?: {
    name: string;
    definition: WorkflowDefinition;
  } | null;
  // Configured values
  configuredValues: [string, unknown][];
  hasConfiguredValues: boolean;
  // x402
  isX402Endpoint: boolean;
  x402Info: {
    url?: string;
  } | null;
  // Data type for special format handling
  dataType?: string;
  slackChannelsMap?: Map<string, SlackChannel>;
  isSlackConnector?: boolean;
  telegramChatsMap?: Map<string, TelegramChat>;
  quickBooksAccountsMap?: Map<string, QBAccount>;
  // Token formatting for vault actions
  inputAmountDecimals?: number;
  resultDecimals?: number;
  vaultShareDecimals?: number;
  resultSchema?: Record<string, unknown>;
  payloadSchema?: Record<string, unknown>;
}

/**
 * Renders the main body content of the ActionNode: output preview, send-erc20,
 * dex-swap, relay-swap, math operation, trigger-workflow, or generic configured values.
 */
export function NodeBodyContent({
  showOutputPreview,
  nodeState,
  executionStatus,
  isSendErc20Token,
  payloadChainId,
  tokenInfo,
  payload,
  chainInfoMap,
  recipientAddress,
  recipientProfile,
  getNodeName,
  isDexSwap,
  tokenInInfo,
  tokenOutInfo,
  isRelaySwap,
  srcChainId,
  dstChainId,
  isMathOperation,
  isTriggerWorkflow,
  targetWorkflowId,
  targetWorkflow,
  configuredValues,
  hasConfiguredValues,
  isX402Endpoint,
  x402Info,
  dataType,
  slackChannelsMap,
  isSlackConnector,
  telegramChatsMap,
  quickBooksAccountsMap,
  inputAmountDecimals,
  resultDecimals,
  vaultShareDecimals,
  resultSchema,
  payloadSchema
}: NodeBodyContentProps) {
  const {
    wallets: organizationWallets
  } = useOrgWalletsContext();
  const tokensMap = React.useMemo(() => {
    const entries: Array<[string, InlineTokenData]> = [tokenInfo, tokenInInfo, tokenOutInfo].filter((token): token is NonNullable<typeof token> => Boolean(token)).map(token_0 => [token_0.address.toLowerCase(), {
      address: token_0.address,
      symbol: token_0.symbol,
      name: token_0.name,
      chainId: token_0.chainId,
      logoUrl: token_0.metadata.logoURI
    }]);
    return entries.length > 0 ? new Map(entries) : undefined;
  }, [tokenInfo, tokenInInfo, tokenOutInfo]);
  const configuredValuesObject = React.useMemo(() => Object.fromEntries(configuredValues) as Record<string, unknown>, [configuredValues]);
  const actionSummary = React.useMemo(() => getActionNodeSummary(dataType, payload, {
    getNodeName,
    chainInfoMap,
    tokenInfo,
    slackChannelsMap,
    telegramChatsMap,
    quickBooksAccountsMap
  }), [chainInfoMap, dataType, getNodeName, payload, quickBooksAccountsMap, slackChannelsMap, telegramChatsMap, tokenInfo]);
  const remainingConfiguredValues = React.useMemo(() => {
    if (!actionSummary?.consumedKeys?.length) {
      return configuredValues;
    }
    const consumed = new Set(actionSummary.consumedKeys);
    return configuredValues.filter(([key]) => !consumed.has(key));
  }, [actionSummary, configuredValues]);

  // Output preview
  if (showOutputPreview) {
    return <NodeOutputPreview result={nodeState?.result} status={executionStatus} resultSchema={resultSchema} tokenDecimals={resultDecimals} vaultShareDecimals={vaultShareDecimals} />;
  }

  // Send ERC20 Token compact display
  if (isSendErc20Token && payloadChainId && tokenInfo) {
    return <div className="border-ds-border-default space-y-2 border-t pt-2">
        <div className="text-ds-text-secondary flex flex-wrap items-center gap-1.5 text-xs">
          <span>Send</span>
          {tokenInfo.metadata.logoURI && <img src={tokenInfo.metadata.logoURI} alt={tokenInfo.symbol} className="h-4 w-4 rounded-full" onError={e => {
          (e.target as HTMLImageElement).style.display = "none";
        }} />}
          {containsTemplateVariable(payload?.amount) ? renderValueWithVariables(payload?.amount) : <span className="text-ds-text-primary font-semibold">
              {formatTokenAmount(payload?.amount as string | undefined, tokenInfo.decimals)}
            </span>}
          <span className="text-ds-text-primary font-semibold">{tokenInfo.symbol}</span>
          <span>on</span>
          {chainInfoMap.get(payloadChainId)?.iconUrl && <img src={chainInfoMap.get(payloadChainId)?.iconUrl || ""} alt="" className="h-4 w-4 rounded-full" />}
          <span className="text-ds-text-primary font-medium">
            {chainInfoMap.get(payloadChainId)?.name || `Chain ${payloadChainId}`}
          </span>
        </div>

        {/* Recipient profile */}
        {recipientAddress && <div className="text-ds-text-secondary flex flex-wrap items-center gap-1.5 text-xs">
            <span>to</span>
            {(() => {
          const matchingWallet = findMatchingOrganizationWallet(recipientAddress, organizationWallets);
          if (matchingWallet) {
            return <OrganizationWalletBadge wallet={matchingWallet} variant="summary" />;
          }
          return <UserAvatar address={recipientProfile?.address || (recipientAddress.startsWith("0x") ? recipientAddress : undefined)} size={16} includeAvatar includeUsername textClassName="font-medium text-ds-text-primary text-xs" imageClassName="h-4 w-4" />;
        })()}
          </div>}
      </div>;
  }

  // DEX Swap compact display
  if (isDexSwap && payloadChainId) {
    return <div className="border-ds-border-default space-y-2 border-t pt-2">
        <div className="text-ds-text-secondary flex flex-wrap items-center gap-1.5 text-xs">
          <span>Swap</span>
          {containsTemplateVariable(payload?.amountIn) ? renderValueWithVariables(payload?.amountIn) : <span className="text-ds-text-primary font-semibold">
              {formatTokenAmount(payload?.amountIn as string | number | undefined, tokenInInfo?.decimals)}
            </span>}
          {tokenInInfo && <>
              <TokenIcon logoUrl={tokenInInfo.metadata.logoURI} symbol={tokenInInfo.symbol} chainId={tokenInInfo.chainId} tokenAddress={tokenInInfo.address} size={16} />
              <span className="text-ds-text-primary font-semibold">{tokenInInfo.symbol}</span>
            </>}
          <span>for</span>
          {tokenOutInfo && <>
              <TokenIcon logoUrl={tokenOutInfo.metadata.logoURI} symbol={tokenOutInfo.symbol} chainId={tokenOutInfo.chainId} tokenAddress={tokenOutInfo.address} size={16} />
              <span className="text-ds-text-primary font-semibold">{tokenOutInfo.symbol}</span>
            </>}
          <span>on</span>
          {chainInfoMap.get(payloadChainId)?.iconUrl && <img src={chainInfoMap.get(payloadChainId)?.iconUrl || ""} alt="" className="h-4 w-4 rounded-full" />}
          <span className="text-ds-text-primary font-medium">
            {chainInfoMap.get(payloadChainId)?.name || `Chain ${payloadChainId}`}
          </span>
        </div>
        {payload?.deadlineMinutes !== undefined && <div className="flex items-start gap-2 text-xs">
            <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">
              Deadline Minutes:
            </span>
            <span className="text-ds-text-secondary truncate">{String(payload.deadlineMinutes)}</span>
          </div>}
        {payload?.slippageBps !== undefined && <div className="flex items-start gap-2 text-xs">
            <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">
              Slippage Bps:
            </span>
            <span className="text-ds-text-secondary truncate">{String(payload.slippageBps)}</span>
          </div>}
      </div>;
  }

  // Relay Cross-Chain Swap compact display
  if (isRelaySwap && srcChainId && dstChainId) {
    const srcIsVariable = containsTemplateVariable(payload?.srcChainId);
    const dstIsVariable = containsTemplateVariable(payload?.dstChainId);
    const tokenInIsVariable = containsTemplateVariable(payload?.tokenIn);
    const tokenOutIsVariable = containsTemplateVariable(payload?.tokenOut);
    const slippageIsVariable = containsTemplateVariable(payload?.slippageBps);
    return <div className="border-ds-border-default space-y-2 border-t pt-2">
        <div className="text-ds-text-secondary flex flex-wrap items-center gap-1.5 text-xs">
          <span>Swap</span>
          {tokenInInfo ? <>
              <TokenIcon logoUrl={tokenInInfo.metadata.logoURI} symbol={tokenInInfo.symbol} chainId={tokenInInfo.chainId} tokenAddress={tokenInInfo.address} size={16} />
              <span className="text-ds-text-primary font-semibold">{tokenInInfo.symbol}</span>
            </> : tokenInIsVariable ? renderValueWithVariables(payload?.tokenIn) : null}
          <span>on</span>
          {srcIsVariable ? renderValueWithVariables(payload?.srcChainId) : <>
              {chainInfoMap.get(srcChainId)?.iconUrl && <img src={chainInfoMap.get(srcChainId)?.iconUrl || ""} alt="" className="h-4 w-4 rounded-full" />}
              <span className="text-ds-text-primary font-medium">
                {chainInfoMap.get(srcChainId)?.name || `Chain ${srcChainId}`}
              </span>
            </>}
        </div>
        <div className="text-ds-text-secondary flex flex-wrap items-center gap-1.5 text-xs">
          <span>for</span>
          {tokenOutIsVariable ? renderValueWithVariables(payload?.tokenOut) : isNativeTokenAddress(payload?.tokenOut) ? <span className="text-ds-text-primary font-semibold">Native Token</span> : tokenOutInfo ? <>
              <TokenIcon logoUrl={tokenOutInfo.metadata.logoURI} symbol={tokenOutInfo.symbol} chainId={tokenOutInfo.chainId} tokenAddress={tokenOutInfo.address} size={16} />
              <span className="text-ds-text-primary font-semibold">{tokenOutInfo.symbol}</span>
            </> : <span className="text-ds-text-primary font-semibold">{String(payload?.tokenOut || "")}</span>}
          <span>on</span>
          {dstIsVariable ? renderValueWithVariables(payload?.dstChainId) : <>
              {chainInfoMap.get(dstChainId)?.iconUrl && <img src={chainInfoMap.get(dstChainId)?.iconUrl || ""} alt="" className="h-4 w-4 rounded-full" />}
              <span className="text-ds-text-primary font-medium">
                {chainInfoMap.get(dstChainId)?.name || `Chain ${dstChainId}`}
              </span>
            </>}
        </div>
        {payload?.slippageBps !== undefined && <div className="flex items-start gap-2 text-xs">
            <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">
              Slippage Bps:
            </span>
            <span className="text-ds-text-secondary truncate">
              {slippageIsVariable ? renderValueWithVariables(payload.slippageBps) : String(payload.slippageBps)}
            </span>
          </div>}
      </div>;
  }

  // Math operation display
  if (isMathOperation) {
    return <div className="border-ds-border-default space-y-1.5 border-t pt-2">
        <code className="rounded bg-blue-50 px-2 py-1 font-mono text-xs text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
          {formatMathOperation(payload?.left ?? payload?.Left, payload?.operator ?? payload?.Operator, payload?.right ?? payload?.Right, payload?.rounding ?? payload?.Rounding)}
        </code>
      </div>;
  }

  // Trigger workflow display
  if (isTriggerWorkflow && targetWorkflowId) {
    return <div className="border-ds-border-default space-y-2 border-t pt-2">
        {/* Triggers workflow name */}
        <div className="text-xs">
          {targetWorkflow ? <Link href={`/workflows/edit?id=${targetWorkflowId}`} className="text-ds-text-secondary font-medium hover:text-blue-600 dark:hover:text-blue-400">
              Triggers{" "}
              <span className="font-semibold text-blue-600 hover:underline dark:text-blue-400">
                {targetWorkflow.name || "Untitled Workflow"}
              </span>
            </Link> : <span className="text-ds-text-muted font-mono">Triggers {targetWorkflowId}</span>}
        </div>
        {/* Minimap preview - full node width */}
        {targetWorkflow?.definition && <Link href={`/workflows/edit?id=${targetWorkflowId}`} className="block">
            <WorkflowMinimap definition={targetWorkflow.definition} width={256} height={80} rounded="lg" className="w-full transition-all hover:ring-2 hover:ring-blue-500/40" />
          </Link>}
        {/* Show payload variables as individual items */}
        {remainingConfiguredValues.length > 0 && (() => {
        // Extract payload entries: if there's a single "payload" object, expand its keys
        const payloadEntry = remainingConfiguredValues.find(([k]) => k === "payload");
        const payloadObj = payloadEntry && typeof payloadEntry[1] === "object" && payloadEntry[1] !== null && !Array.isArray(payloadEntry[1]) ? payloadEntry[1] as Record<string, unknown> : null;
        const expandedEntries = payloadObj ? Object.entries(payloadObj).filter(([, v]) => v !== null && v !== undefined && v !== "") : null;
        // Other configured values that aren't the payload object
        const otherEntries = remainingConfiguredValues.filter(([k_0]) => k_0 !== "payload" || !payloadObj);
        return <div className="space-y-1.5">
                {expandedEntries && expandedEntries.length > 0 && <div className="space-y-1">
                    {expandedEntries.map(([key_0, value]) => <ConfiguredValueRow key={key_0} fieldKey={key_0} value={value} chainInfoMap={chainInfoMap} tokenInfo={tokenInfo} tokenInInfo={tokenInInfo} organizationWallets={organizationWallets} tokensMap={tokensMap} parentObject={payloadObj ?? undefined} getNodeName={getNodeName} dataType={dataType} payload={payload} slackChannelsMap={slackChannelsMap} isSlackConnector={isSlackConnector} telegramChatsMap={telegramChatsMap} quickBooksAccountsMap={quickBooksAccountsMap} inputAmountDecimals={inputAmountDecimals} />)}
                  </div>}
                {otherEntries.map(([key_1, value_0]) => <ConfiguredValueRow key={key_1} fieldKey={key_1} value={value_0} chainInfoMap={chainInfoMap} tokenInfo={tokenInfo} tokenInInfo={tokenInInfo} organizationWallets={organizationWallets} tokensMap={tokensMap} parentObject={configuredValuesObject} getNodeName={getNodeName} dataType={dataType} payload={payload} slackChannelsMap={slackChannelsMap} isSlackConnector={isSlackConnector} telegramChatsMap={telegramChatsMap} quickBooksAccountsMap={quickBooksAccountsMap} inputAmountDecimals={inputAmountDecimals} fieldFormat={getSchemaFieldFormat(payloadSchema, key_1)} />)}
              </div>;
      })()}
      </div>;
  }

  // Generic configured values display
  if (hasConfiguredValues || actionSummary) {
    return <div className="border-ds-border-default space-y-1.5 border-t pt-2">
        {actionSummary && <div className="space-y-1">
            <div className="body-xs leading-5">{actionSummary.summary}</div>
            {actionSummary.auxiliary && <div className="body-xs">{actionSummary.auxiliary}</div>}
          </div>}
        {remainingConfiguredValues.map(([key_2, value_1]) => <ConfiguredValueRow key={key_2} fieldKey={key_2} value={value_1} chainInfoMap={chainInfoMap} tokenInfo={tokenInfo} tokenInInfo={tokenInInfo} organizationWallets={organizationWallets} tokensMap={tokensMap} parentObject={configuredValuesObject} getNodeName={getNodeName} dataType={dataType} payload={payload} slackChannelsMap={slackChannelsMap} isSlackConnector={isSlackConnector} telegramChatsMap={telegramChatsMap} quickBooksAccountsMap={quickBooksAccountsMap} inputAmountDecimals={inputAmountDecimals} fieldFormat={getSchemaFieldFormat(payloadSchema, key_2)} />)}
        {/* x402 endpoint URL when node has configured values */}
        {isX402Endpoint && x402Info?.url && <div className={cn("border-ds-border-default pt-2", (actionSummary || remainingConfiguredValues.length > 0) && "mt-2 border-t")}>
            <div className="label-xs flex items-center gap-1">
              <IconArrowUpRightOutline18 className="h-3 w-3 flex-shrink-0" />
              <span className="truncate" title={x402Info.url}>
                {formatDisplayUrl(x402Info.url)}
              </span>
            </div>
          </div>}
      </div>;
  }

  // x402 endpoint URL fallback (no configured values)
  if (isX402Endpoint && x402Info?.url) {
    return <div className="border-ds-border-default border-t pt-2">
        <div className="label-xs flex items-center gap-1">
          <IconArrowUpRightOutline18 className="h-3 w-3 flex-shrink-0" />
          <span className="truncate" title={x402Info.url}>
            {formatDisplayUrl(x402Info.url)}
          </span>
        </div>
      </div>;
  }
  return null;
}

// ─── Single Configured Value Row ────────────────────────────────────────────

interface ConfiguredValueRowProps {
  fieldKey: string;
  value: unknown;
  chainInfoMap: Map<number, ChainInfo>;
  tokenInfo: TokenInfo | null;
  tokenInInfo: TokenInfo | null;
  organizationWallets: OrganizationWallet[];
  tokensMap?: Map<string, InlineTokenData>;
  parentObject?: Record<string, unknown>;
  getNodeName: (nodeId: string) => string | undefined;
  dataType?: string;
  /** Full payload — needed for actions where amount decimals depend on other fields (e.g., place-order orderType). */
  payload?: Record<string, unknown>;
  slackChannelsMap?: Map<string, SlackChannel>;
  isSlackConnector?: boolean;
  telegramChatsMap?: Map<string, TelegramChat>;
  quickBooksAccountsMap?: Map<string, QBAccount>;
  /** Resolved decimals for the input amount field (e.g., vault shares vs underlying asset) */
  inputAmountDecimals?: number;
  /** Schema-declared format for this field (e.g., "currency" → renders with $ prefix + commas). */
  fieldFormat?: string;
}

/**
 * Renders a single configured value row within the generic values list.
 * Handles special formatting for chains, addresses, tokens, RRULE, hex colors, etc.
 */
function ConfiguredValueRow(t0) {
  const $ = _c(169);
  const {
    fieldKey: key,
    value,
    chainInfoMap,
    tokenInfo,
    tokenInInfo,
    organizationWallets,
    tokensMap,
    parentObject,
    getNodeName,
    dataType,
    payload: fullPayload,
    slackChannelsMap,
    isSlackConnector,
    telegramChatsMap,
    quickBooksAccountsMap,
    inputAmountDecimals,
    fieldFormat
  } = t0;
  let extraInfo;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== chainInfoMap || $[1] !== dataType || $[2] !== fieldFormat || $[3] !== fullPayload || $[4] !== getNodeName || $[5] !== inputAmountDecimals || $[6] !== isSlackConnector || $[7] !== key || $[8] !== organizationWallets || $[9] !== parentObject || $[10] !== quickBooksAccountsMap || $[11] !== slackChannelsMap || $[12] !== telegramChatsMap || $[13] !== tokenInInfo || $[14] !== tokenInfo || $[15] !== tokensMap || $[16] !== value) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const displayValue = fieldFormat === "currency" && (typeof value === "number" || typeof value === "string") ? formatCurrencyUsd(value) : formatValue(value);
      const safeDisplayValue = isHexLikeValue(value) ? truncateHexValue(value) : displayValue;
      let t7;
      if ($[24] !== key) {
        t7 = formatLabel(key);
        $[24] = key;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      let label = t7;
      const isRRule = key === "rrule" && typeof value === "string";
      const isDelay = (key.toLowerCase().includes("delay") || key.toLowerCase().includes("ms")) && typeof value === "number";
      const isChainId = isChainIdKey(key) && typeof value === "number";
      const isChainIdsArray = key === "chainIds" && Array.isArray(value);
      const isFunctionName = key === "functionName";
      const isSlackConversation = (key === "conversation" || key === "channel") && typeof value === "string" && !!isSlackConnector;
      const isSlackConversationsArray = key === "conversations" && Array.isArray(value) && !!isSlackConnector;
      const isTelegramChat = key === "chatId";
      const isQuickBooksAccount = key === "accountId" || key === "expenseAccountId";
      const matchingWallet = typeof value === "string" ? findMatchingOrganizationWallet(value, organizationWallets) : undefined;
      const shouldRenderEnhancedInlineValue = typeof value === "string" && (Boolean(matchingWallet) || isEthAddress(value));
      if (isChainId) {
        label = "Chain";
      }
      if (isFunctionName) {
        label = "Function Name";
      }
      if (isSlackConversation) {
        label = "Channel";
      }
      if (isSlackConversationsArray) {
        label = "Channels";
      }
      if (isTelegramChat) {
        label = "Chat";
      }
      if (key === "accountId") {
        label = "Payment Account";
      }
      if (key === "expenseAccountId") {
        label = "Expense Account";
      }
      if (key === "coin") {
        label = "Asset";
      }
      if (key === "tokenAddress") {
        label = "Token";
      }
      if (key === "vaultAddress") {
        label = "Vault";
      }
      if (isFunctionName) {
        let t8;
        if ($[26] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium">{label}:</span>;
          $[26] = label;
          $[27] = t8;
        } else {
          t8 = $[27];
        }
        t6 = <div className="flex items-start gap-2 overflow-hidden text-xs">{t8}<span className="min-w-0 truncate font-mono text-xs font-medium text-blue-600 dark:text-blue-400" title={displayValue}>{displayValue}</span></div>;
        break bb0;
      }
      if (key === "data" && dataType === "format") {
        const templateVars = extractTemplateVariableNames(value);
        let t8;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium">Data:</span>;
          $[28] = t8;
        } else {
          t8 = $[28];
        }
        const t9 = templateVars.length > 0 ? templateVars.map(_temp6) : <span className="text-ds-text-secondary truncate" title={tooltipText(safeDisplayValue)}>{safeDisplayValue.length > 30 ? `${safeDisplayValue.slice(0, 30)}...` : safeDisplayValue}</span>;
        let t10;
        if ($[29] !== t9) {
          t10 = <div className="flex items-start gap-2 overflow-hidden text-xs">{t8}<div className="flex min-w-0 flex-wrap gap-1">{t9}</div></div>;
          $[29] = t9;
          $[30] = t10;
        } else {
          t10 = $[30];
        }
        t6 = t10;
        break bb0;
      }
      if (key === "template" || key === "message") {
        let t8;
        if ($[31] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled font-medium capitalize">{label}:</span>;
          $[31] = label;
          $[32] = t8;
        } else {
          t8 = $[32];
        }
        t6 = <div className="space-y-1 overflow-hidden text-xs">{t8}<TemplateMarkdownPreview content={displayValue} getNodeName={getNodeName} /></div>;
        break bb0;
      }
      if (isChainIdsArray) {
        const t8 = value as unknown[];
        let t10;
        let t11;
        let t12;
        let t9;
        if ($[33] !== chainInfoMap || $[34] !== t8) {
          const chainIdsArr = t8.map(_temp7).filter(_temp8);
          t11 = "flex items-start gap-2 text-xs";
          const t13 = chainIdsArr.length === 1 ? "Chain:" : "Chains:";
          if ($[39] !== t13) {
            t12 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium">{t13}</span>;
            $[39] = t13;
            $[40] = t12;
          } else {
            t12 = $[40];
          }
          t9 = "flex flex-wrap gap-1.5";
          let t14;
          if ($[41] !== chainInfoMap) {
            t14 = chainId_1 => {
              const chain = chainInfoMap.get(chainId_1);
              return chain ? <ChainBadge key={chainId_1} chain={chain} size="sm" variant="summary" /> : <span key={chainId_1} className="text-ds-text-secondary font-mono">{chainId_1}</span>;
            };
            $[41] = chainInfoMap;
            $[42] = t14;
          } else {
            t14 = $[42];
          }
          t10 = chainIdsArr.map(t14);
          $[33] = chainInfoMap;
          $[34] = t8;
          $[35] = t10;
          $[36] = t11;
          $[37] = t12;
          $[38] = t9;
        } else {
          t10 = $[35];
          t11 = $[36];
          t12 = $[37];
          t9 = $[38];
        }
        let t13;
        if ($[43] !== t10 || $[44] !== t9) {
          t13 = <div className={t9}>{t10}</div>;
          $[43] = t10;
          $[44] = t9;
          $[45] = t13;
        } else {
          t13 = $[45];
        }
        let t14;
        if ($[46] !== t11 || $[47] !== t12 || $[48] !== t13) {
          t14 = <div className={t11}>{t12}{t13}</div>;
          $[46] = t11;
          $[47] = t12;
          $[48] = t13;
          $[49] = t14;
        } else {
          t14 = $[49];
        }
        t6 = t14;
        break bb0;
      }
      if (isSlackConversation) {
        let t8;
        if ($[50] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium">{label}:</span>;
          $[50] = label;
          $[51] = t8;
        } else {
          t8 = $[51];
        }
        let t9;
        if ($[52] !== slackChannelsMap || $[53] !== value) {
          t9 = <SlackConversationBadge value={value} slackChannelsMap={slackChannelsMap} />;
          $[52] = slackChannelsMap;
          $[53] = value;
          $[54] = t9;
        } else {
          t9 = $[54];
        }
        let t10;
        if ($[55] !== t8 || $[56] !== t9) {
          t10 = <div className="flex items-start gap-2 text-xs">{t8}{t9}</div>;
          $[55] = t8;
          $[56] = t9;
          $[57] = t10;
        } else {
          t10 = $[57];
        }
        t6 = t10;
        break bb0;
      }
      if (isSlackConversationsArray) {
        const t8 = value as unknown[];
        let t10;
        let t11;
        let t12;
        let t9;
        if ($[58] !== label || $[59] !== slackChannelsMap || $[60] !== t8) {
          const items = t8.filter(_temp9);
          t11 = "flex items-start gap-2 text-xs";
          if ($[65] !== label) {
            t12 = <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium">{label}:</span>;
            $[65] = label;
            $[66] = t12;
          } else {
            t12 = $[66];
          }
          t9 = "flex flex-wrap gap-1.5";
          let t13;
          if ($[67] !== slackChannelsMap) {
            t13 = item_0 => <SlackConversationBadge key={item_0} value={item_0} slackChannelsMap={slackChannelsMap} />;
            $[67] = slackChannelsMap;
            $[68] = t13;
          } else {
            t13 = $[68];
          }
          t10 = items.map(t13);
          $[58] = label;
          $[59] = slackChannelsMap;
          $[60] = t8;
          $[61] = t10;
          $[62] = t11;
          $[63] = t12;
          $[64] = t9;
        } else {
          t10 = $[61];
          t11 = $[62];
          t12 = $[63];
          t9 = $[64];
        }
        let t13;
        if ($[69] !== t10 || $[70] !== t9) {
          t13 = <div className={t9}>{t10}</div>;
          $[69] = t10;
          $[70] = t9;
          $[71] = t13;
        } else {
          t13 = $[71];
        }
        let t14;
        if ($[72] !== t11 || $[73] !== t12 || $[74] !== t13) {
          t14 = <div className={t11}>{t12}{t13}</div>;
          $[72] = t11;
          $[73] = t12;
          $[74] = t13;
          $[75] = t14;
        } else {
          t14 = $[75];
        }
        t6 = t14;
        break bb0;
      }
      if (isTelegramChat) {
        let t8;
        if ($[76] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium">{label}:</span>;
          $[76] = label;
          $[77] = t8;
        } else {
          t8 = $[77];
        }
        let t9;
        if ($[78] !== telegramChatsMap || $[79] !== value) {
          t9 = <TelegramChatBadge value={value} telegramChatsMap={telegramChatsMap} />;
          $[78] = telegramChatsMap;
          $[79] = value;
          $[80] = t9;
        } else {
          t9 = $[80];
        }
        let t10;
        if ($[81] !== t8 || $[82] !== t9) {
          t10 = <div className="flex items-start gap-2 text-xs">{t8}{t9}</div>;
          $[81] = t8;
          $[82] = t9;
          $[83] = t10;
        } else {
          t10 = $[83];
        }
        t6 = t10;
        break bb0;
      }
      if (isQuickBooksAccount) {
        let t8;
        if ($[84] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium">{label}:</span>;
          $[84] = label;
          $[85] = t8;
        } else {
          t8 = $[85];
        }
        let t9;
        if ($[86] !== quickBooksAccountsMap || $[87] !== value) {
          t9 = <QuickBooksAccountBadge value={value} quickBooksAccountsMap={quickBooksAccountsMap} />;
          $[86] = quickBooksAccountsMap;
          $[87] = value;
          $[88] = t9;
        } else {
          t9 = $[88];
        }
        let t10;
        if ($[89] !== t8 || $[90] !== t9) {
          t10 = <div className="flex items-start gap-2 text-xs">{t8}{t9}</div>;
          $[89] = t8;
          $[90] = t9;
          $[91] = t10;
        } else {
          t10 = $[91];
        }
        t6 = t10;
        break bb0;
      }
      if (shouldRenderEnhancedInlineValue) {
        let t8;
        if ($[92] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium">{label}:</span>;
          $[92] = label;
          $[93] = t8;
        } else {
          t8 = $[93];
        }
        let t9;
        if ($[94] !== key || $[95] !== organizationWallets || $[96] !== parentObject || $[97] !== tokensMap || $[98] !== value) {
          t9 = <div className="min-w-0 flex-1"><InlineDetectedValue value={value} keyName={key} parentObject={parentObject} wallets={organizationWallets} tokens={tokensMap} variant="summary" /></div>;
          $[94] = key;
          $[95] = organizationWallets;
          $[96] = parentObject;
          $[97] = tokensMap;
          $[98] = value;
          $[99] = t9;
        } else {
          t9 = $[99];
        }
        let t10;
        if ($[100] !== t8 || $[101] !== t9) {
          t10 = <div className="flex items-start gap-2 overflow-hidden text-xs">{t8}{t9}</div>;
          $[100] = t8;
          $[101] = t9;
          $[102] = t10;
        } else {
          t10 = $[102];
        }
        t6 = t10;
        break bb0;
      }
      const isOrderAction = dataType === "place-order" || dataType === "track-position";
      const isUsdField = key === "targetPriceUsd" || key === "entryCost";
      if (isOrderAction && (isUsdField || key === "amount" && fullPayload?.orderType === "buy") && (typeof value === "string" || typeof value === "number") && !String(value).includes("{{")) {
        const suffix = isUsdField ? " USD" : " USDC";
        let t8;
        if ($[103] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
          $[103] = label;
          $[104] = t8;
        } else {
          t8 = $[104];
        }
        let t9;
        if ($[105] !== suffix || $[106] !== value) {
          t9 = containsTemplateVariable(value) ? renderValueWithVariables(value) : formatTokenAmount(value as string | number, 6) + suffix;
          $[105] = suffix;
          $[106] = value;
          $[107] = t9;
        } else {
          t9 = $[107];
        }
        const t10 = <span className="text-ds-text-secondary min-w-0 truncate" title={tooltipText(displayValue)}>{t9}</span>;
        let t11;
        if ($[108] !== t10 || $[109] !== t8) {
          t11 = <div className="flex items-center gap-2 overflow-hidden text-xs">{t8}{t10}</div>;
          $[108] = t10;
          $[109] = t8;
          $[110] = t11;
        } else {
          t11 = $[110];
        }
        t6 = t11;
        break bb0;
      }
      if (AMOUNT_FIELDS.has(key) && (typeof value === "string" || typeof value === "number") && !String(value).includes("{{")) {
        let decimals;
        if (key === "amountIn" && tokenInInfo) {
          decimals = tokenInInfo.decimals;
        } else {
          if (key === "amount" && inputAmountDecimals != null) {
            decimals = inputAmountDecimals;
          } else {
            if (tokenInfo) {
              decimals = tokenInfo.decimals;
            } else {
              const addressFields = ["vaultAddress", "tokenAddress", "token"] as const;
              const hasVariableAddress = addressFields.some(f => containsTemplateVariable(fullPayload?.[f]));
              const vaultResolutionFailed = typeof fullPayload?.vaultAddress === "string" && fullPayload.vaultAddress.startsWith("0x") && !containsTemplateVariable(fullPayload.vaultAddress);
              const payloadChainId = typeof fullPayload?.chainId === "number" ? fullPayload.chainId : undefined;
              const hasAnyAddressField = addressFields.some(f_0 => typeof fullPayload?.[f_0] === "string" && (fullPayload[f_0] as string).startsWith("0x"));
              if (!hasVariableAddress && !vaultResolutionFailed && (payloadChainId != null || hasAnyAddressField)) {
                let t8;
                if ($[111] !== payloadChainId) {
                  t8 = getNativeTokenDecimals(payloadChainId);
                  $[111] = payloadChainId;
                  $[112] = t8;
                } else {
                  t8 = $[112];
                }
                decimals = t8;
              }
            }
          }
        }
        if (decimals != null) {
          const symbol = key === "amountIn" ? tokenInInfo?.symbol : tokenInfo?.symbol;
          const symbolSuffix = symbol ? ` ${symbol}` : "";
          let t8;
          if ($[113] !== label) {
            t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
            $[113] = label;
            $[114] = t8;
          } else {
            t8 = $[114];
          }
          const t9 = displayValue + symbolSuffix;
          let t10;
          if ($[115] !== t9) {
            t10 = tooltipText(t9);
            $[115] = t9;
            $[116] = t10;
          } else {
            t10 = $[116];
          }
          let t11;
          if ($[117] !== decimals || $[118] !== value) {
            t11 = containsTemplateVariable(value) ? renderValueWithVariables(value) : formatTokenAmount(value as string | number, decimals);
            $[117] = decimals;
            $[118] = value;
            $[119] = t11;
          } else {
            t11 = $[119];
          }
          let t12;
          if ($[120] !== symbolSuffix || $[121] !== t10 || $[122] !== t11) {
            t12 = <span className="text-ds-text-secondary min-w-0 truncate" title={t10}>{t11}{symbolSuffix}</span>;
            $[120] = symbolSuffix;
            $[121] = t10;
            $[122] = t11;
            $[123] = t12;
          } else {
            t12 = $[123];
          }
          let t13;
          if ($[124] !== t12 || $[125] !== t8) {
            t13 = <div className="flex items-center gap-2 overflow-hidden text-xs">{t8}{t12}</div>;
            $[124] = t12;
            $[125] = t8;
            $[126] = t13;
          } else {
            t13 = $[126];
          }
          t6 = t13;
          break bb0;
        }
      }
      if (isChainId) {
        let t8;
        if ($[127] !== chainInfoMap || $[128] !== value) {
          t8 = chainInfoMap.get(value as number);
          $[127] = chainInfoMap;
          $[128] = value;
          $[129] = t8;
        } else {
          t8 = $[129];
        }
        const chain_0 = t8;
        let t9;
        if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <span className="text-ds-text-subtle dark:text-ds-text-disabled inline-flex min-h-6 min-w-0 flex-shrink-0 items-center font-medium">Chain:</span>;
          $[130] = t9;
        } else {
          t9 = $[130];
        }
        let t10;
        if ($[131] !== chain_0 || $[132] !== value) {
          t10 = <div className="flex items-start gap-2 text-xs">{t9}{chain_0 ? <ChainBadge chain={chain_0} size="sm" variant="summary" /> : <span className="text-ds-text-secondary font-mono">{value}</span>}</div>;
          $[131] = chain_0;
          $[132] = value;
          $[133] = t10;
        } else {
          t10 = $[133];
        }
        t6 = t10;
        break bb0;
      }
      if (isRRule) {
        let t8;
        if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <div className="flex items-start gap-2 text-xs"><span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">Schedule:</span></div>;
          $[134] = t8;
        } else {
          t8 = $[134];
        }
        const t9 = value as string;
        let t10;
        if ($[135] !== t9) {
          t10 = formatRRule(t9);
          $[135] = t9;
          $[136] = t10;
        } else {
          t10 = $[136];
        }
        let t11;
        if ($[137] !== t10) {
          t11 = <div className="space-y-0.5">{t8}<div className="text-ds-text-muted pl-0 text-xs italic">{t10}</div></div>;
          $[137] = t10;
          $[138] = t11;
        } else {
          t11 = $[138];
        }
        t6 = t11;
        break bb0;
      }
      if (isHexColor(value)) {
        let t8;
        if ($[139] !== label) {
          t8 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
          $[139] = label;
          $[140] = t8;
        } else {
          t8 = $[140];
        }
        let t9;
        if ($[141] !== value) {
          t9 = {
            backgroundColor: value
          };
          $[141] = value;
          $[142] = t9;
        } else {
          t9 = $[142];
        }
        let t10;
        if ($[143] !== t9 || $[144] !== value) {
          t10 = <span className="border-ds-neutral-300 h-3.5 w-3.5 flex-shrink-0 rounded-full border dark:border-zinc-600" style={t9} title={value} />;
          $[143] = t9;
          $[144] = value;
          $[145] = t10;
        } else {
          t10 = $[145];
        }
        let t11;
        if ($[146] !== value) {
          t11 = <span className="text-ds-text-secondary font-mono">{value}</span>;
          $[146] = value;
          $[147] = t11;
        } else {
          t11 = $[147];
        }
        let t12;
        if ($[148] !== t10 || $[149] !== t11) {
          t12 = <div className="flex items-center gap-1.5">{t10}{t11}</div>;
          $[148] = t10;
          $[149] = t11;
          $[150] = t12;
        } else {
          t12 = $[150];
        }
        let t13;
        if ($[151] !== t12 || $[152] !== t8) {
          t13 = <div className="flex items-center gap-2 text-xs">{t8}{t12}</div>;
          $[151] = t12;
          $[152] = t8;
          $[153] = t13;
        } else {
          t13 = $[153];
        }
        t6 = t13;
        break bb0;
      }
      extraInfo = isDelay ? ` (${formatDelayTime(value as number)})` : "";
      const hasVariables = typeof value === "string" && value.includes("{{");
      t4 = "flex items-center gap-2 overflow-hidden text-xs";
      if ($[154] !== label) {
        t5 = <span className="text-ds-text-subtle dark:text-ds-text-disabled min-w-0 flex-shrink-0 font-medium capitalize">{label}:</span>;
        $[154] = label;
        $[155] = t5;
      } else {
        t5 = $[155];
      }
      t1 = "text-ds-text-secondary min-w-0 flex-1 truncate";
      const t8 = displayValue + extraInfo;
      if ($[156] !== t8) {
        t2 = tooltipText(t8);
        $[156] = t8;
        $[157] = t2;
      } else {
        t2 = $[157];
      }
      t3 = hasVariables ? renderWithVariableHighlights(displayValue, getNodeName) : safeDisplayValue;
    }
    $[0] = chainInfoMap;
    $[1] = dataType;
    $[2] = fieldFormat;
    $[3] = fullPayload;
    $[4] = getNodeName;
    $[5] = inputAmountDecimals;
    $[6] = isSlackConnector;
    $[7] = key;
    $[8] = organizationWallets;
    $[9] = parentObject;
    $[10] = quickBooksAccountsMap;
    $[11] = slackChannelsMap;
    $[12] = telegramChatsMap;
    $[13] = tokenInInfo;
    $[14] = tokenInfo;
    $[15] = tokensMap;
    $[16] = value;
    $[17] = extraInfo;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    extraInfo = $[17];
    t1 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[158] !== extraInfo) {
    t7 = extraInfo && <span className="text-ds-text-muted">{extraInfo}</span>;
    $[158] = extraInfo;
    $[159] = t7;
  } else {
    t7 = $[159];
  }
  let t8;
  if ($[160] !== t1 || $[161] !== t2 || $[162] !== t3 || $[163] !== t7) {
    t8 = <span className={t1} title={t2}>{t3}{t7}</span>;
    $[160] = t1;
    $[161] = t2;
    $[162] = t3;
    $[163] = t7;
    $[164] = t8;
  } else {
    t8 = $[164];
  }
  let t9;
  if ($[165] !== t4 || $[166] !== t5 || $[167] !== t8) {
    t9 = <div className={t4}>{t5}{t8}</div>;
    $[165] = t4;
    $[166] = t5;
    $[167] = t8;
    $[168] = t9;
  } else {
    t9 = $[168];
  }
  return t9;
}
function _temp9(item) {
  return typeof item === "string";
}
function _temp8(chainId_0) {
  return Number.isFinite(chainId_0) && chainId_0 > 0;
}
function _temp7(chainId) {
  return typeof chainId === "number" ? chainId : Number(chainId);
}
function _temp6(varName) {
  return <span key={varName} className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-xs font-medium text-blue-600 dark:bg-blue-950/30 dark:text-blue-400">{varName}</span>;
}
function SlackConversationBadge(t0) {
  const $ = _c(12);
  const {
    value,
    slackChannelsMap
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="align-middle">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  let t2;
  if ($[4] !== slackChannelsMap || $[5] !== value) {
    const conversation = typeof value === "string" ? resolveSlackConversationLabel(value, slackChannelsMap) : "Slack channel";
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = cn(inlineReferenceBadgeClassName, "max-w-full");
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    t2 = typeof conversation === "string" && conversation.trim() ? conversation : "Slack channel";
    $[4] = slackChannelsMap;
    $[5] = value;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  let t3;
  if ($[9] !== t1 || $[10] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function TelegramChatBadge(t0) {
  const $ = _c(10);
  const {
    value,
    telegramChatsMap
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="align-middle">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== telegramChatsMap || $[5] !== value) {
    t1 = resolveTelegramChatLabel(String(value ?? ""), telegramChatsMap) || "Telegram chat";
    $[4] = telegramChatsMap;
    $[5] = value;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const chat = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(inlineReferenceBadgeClassName, "max-w-full");
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== chat) {
    t3 = <span className={t2}>{chat}</span>;
    $[8] = chat;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function QuickBooksAccountBadge(t0) {
  const $ = _c(18);
  const {
    value,
    quickBooksAccountsMap
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span className="align-middle">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  const rawValue = typeof value === "string" || typeof value === "number" ? String(value) : "";
  let t1;
  if ($[4] !== quickBooksAccountsMap || $[5] !== rawValue) {
    t1 = rawValue ? quickBooksAccountsMap?.get(rawValue) : undefined;
    $[4] = quickBooksAccountsMap;
    $[5] = rawValue;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const account = t1;
  let t2;
  if ($[7] !== quickBooksAccountsMap || $[8] !== rawValue) {
    t2 = rawValue ? resolveQuickBooksAccountLabel(rawValue, quickBooksAccountsMap) || "QuickBooks account" : "QuickBooks account";
    $[7] = quickBooksAccountsMap;
    $[8] = rawValue;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const label = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(inlineReferenceBadgeClassName, "max-w-full gap-1");
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== label) {
    t4 = <span>{label}</span>;
    $[11] = label;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== account) {
    t5 = account ? <span className={cn("text-[0.85em]", inlineReferenceBadgeAccentClassName)}>#{account.Id}</span> : null;
    $[13] = account;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <span className={t3}>{t4}{t5}</span>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}